import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { fromJS, List } from 'immutable';

import { updateSharedVoicemail } from 'flow/actions/pbx-actions';
import { fetchUserSounds } from 'flow/actions/sounds-actions';
import { MiniAudioPlayer } from 'flow/components/AudioPlayers';
import Page from 'flow/components/Setting/Page';
import Loading from 'flow/components/ui-components/Loading';
import SettingSelectableItem from 'flow/components/ui-components/SettingSelectableItem';
import { VoicemailKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { Sound } from 'flow/types/Sound';

import { TypedMap } from 'flow/types/TypedMap';
import { homeActionUrlPath } from 'flow/utils';

const PbxSharedVoicemailSound = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { voicemailKey } = useParams<{ voicemailKey: VoicemailKey }>();

    const voicemailDTO = useAppSelector((state) =>
        state.pbx.getIn(['voicemails', voicemailKey]),
    );

    const sounds = useAppSelector((state) => state.sounds.get('sounds'));

    useEffect(() => {
        dispatch(fetchUserSounds());
    }, []);

    if (!sounds || !voicemailDTO) {
        return <Loading />;
    }

    const handleBack = () =>
        history.push(
            generatePath(routes['pbx.voicemails.entityKey.settings'], {
                entityKey: voicemailKey,
            }),
        );

    const onSelected = (key: string) => {
        const soundDTO = {
            key,
        };

        const updateFriendlyVoicemailDTO = voicemailDTO
            .setIn(['settings', 'busySound'], fromJS(soundDTO))
            .setIn(['settings', 'unavailSound'], fromJS(soundDTO))
            .delete('voiceMessages');

        dispatch(
            updateSharedVoicemail(voicemailKey, updateFriendlyVoicemailDTO),
        );
        handleBack();
    };

    const publicUserVoicemailSounds = sounds.filter(
        (soundDTO) => soundDTO.get('privateSound') !== true,
    );

    const standardVoicemailSound = voicemailDTO.getIn(
        ['settings', 'standardVoicemailSound'],
        List(),
    );

    const selectableVoicemailSounds = List.of(
        standardVoicemailSound,
        ...publicUserVoicemailSounds,
    );

    return (
        <Page header="pbx.sound" onClickBack={handleBack}>
            <div className="gap-4">
                {selectableVoicemailSounds
                    .valueSeq()
                    .map((sound: TypedMap<Sound>) => {
                        const playbackUrl = homeActionUrlPath(
                            `/${sound.get('playbackURL')}`,
                        );

                        return (
                            <SettingSelectableItem
                                key={sound.get('key')}
                                value={sound.get('key')}
                                onClick={() => onSelected(sound.get('key'))}
                                selected={voicemailDTO.getIn([
                                    'settings',
                                    'busySound',
                                    'key',
                                ])}
                            >
                                <div className="flex items-center gap-1">
                                    <div>
                                        {playbackUrl ? (
                                            <MiniAudioPlayer
                                                url={playbackUrl}
                                                uniqueId={sound.get('key')}
                                            />
                                        ) : null}
                                    </div>

                                    <div>{sound.get('name')}</div>
                                </div>
                            </SettingSelectableItem>
                        );
                    })}
            </div>
        </Page>
    );
};

export default PbxSharedVoicemailSound;
